import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaPaperPlane } from "react-icons/fa";
import { MdChatBubble } from "react-icons/md";
import { IoMdClose } from "react-icons/io";

export default function ChatBox() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);

  useEffect(() => {
    const handleOpenChat = () => setOpen(true);
    window.addEventListener("openChat", handleOpenChat);
    return () => window.removeEventListener("openChat", handleOpenChat);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        open &&
        chatRef.current &&
        !chatRef.current.contains(e.target) &&
        !e.target.closest(".chat-toggle-btn")
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const sendMessage = async () => {
    if (!input.trim()) return;
    const userMsg = { sender: "user", text: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch("/.netlify/functions/askAI", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: input }),
      });
      if (res.ok) {
        const data = await res.json();
        const aiMsg = { sender: "ai", text: data.reply };
        setMessages((prev) => [...prev, aiMsg]);
      }
      else {
        throw new Error(res)
      }
      
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { sender: "ai", text: `Sorry! ${process.env.REACT_APP_ASSISTANT_NAME ?? "Random"} is asleep 😴.`},
      ]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Floating chat button */}
        <button
        onClick={() => setOpen(!open)}
        className="chat-toggle-btn fixed bottom-6 right-6 bg-gray-700 text-white p-4 rounded-full shadow-lg z-50 hover:bg-gray-800 transition-transform duration-300 transform hover:scale-110 flex items-center justify-center"
        >
        <span
            className={`transition-transform duration-500 ${
            open ? "rotate-[45deg]" : "rotate-0"
            }`}
        >
            {open ? <IoMdClose size={24} className="rotate-[45deg]"/> : <MdChatBubble size={24} />}
        </span>
        </button>

      {/* Chat window */}
      <div
        ref={chatRef}
        className={`fixed right-6 w-80 bg-white border border-gray-300 rounded-xl shadow-lg flex flex-col z-50
          transform transition-all duration-500
          ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"}
          bottom-[100px]`} 
      >
        {/* Header */}
        <div
          onClick={() => setOpen(false)}
          className="bg-gray-800 text-white p-3 rounded-t-xl font-semibold text-center cursor-pointer select-none"
        >
          Ask {process.env.REACT_APP_ASSISTANT_NAME ?? "Random"}
        </div>

        {/* Messages */}
        <div className="flex-1 max-h-72 overflow-y-auto p-3 space-y-3 bg-gray-50">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`p-2 rounded-lg break-words ${
                msg.sender === "user"
                  ? "bg-gray-300 text-right self-end"
                  : "bg-white text-left shadow-sm"
              }`}
            >
              {msg.sender === "ai" ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: (props) => <p className="m-0" {...props} />,
                    strong: (props) => <strong className="font-semibold" {...props} />,
                    em: (props) => <em className="italic" {...props} />,
                    code: (props) => (
                      <code className="bg-gray-200 px-1 py-0.5 rounded text-sm font-mono" {...props} />
                    ),
                    a: (props) => (
                      // eslint-disable-next-line jsx-a11y/anchor-has-content
                      <a className="text-blue-600 underline" {...props} target="_blank" rel="noopener noreferrer" />
                    ),
                    li: (props) => <li className="ml-4 list-disc" {...props} />,
                    table: (props) => (
                      <table className="border border-gray-300 w-full text-left mb-2" {...props} />
                    ),
                    th: (props) => (
                      <th className="border border-gray-300 px-2 py-1 bg-gray-100" {...props} />
                    ),
                    td: (props) => (
                      <td className="border border-gray-300 px-2 py-1" {...props} />
                    ),
                    thead: (props) => <thead className="bg-gray-100" {...props} />,
                    tbody: (props) => <tbody {...props} />,
                  }}
                >
                  {msg.text}
                </ReactMarkdown>
              ) : (
                msg.text
              )}
            </div>
          ))}
          {loading && <div className="text-gray-500 italic">Thinking...</div>}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="flex p-3 border-t border-gray-300 bg-gray-50 rounded-b-xl">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            className="flex-1 border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-gray-400"
            placeholder="Ask me anything about Abin..."
          />
          <button
            onClick={sendMessage}
            className="ml-2 bg-gray-800 text-white px-3 rounded-lg hover:bg-gray-900 transition flex items-center justify-center"
          >
            <FaPaperPlane />
          </button>
        </div>
      </div>
    </>
  );
}
